// All share-related analytics live here. Components never call onEvent for
// share events — they pass a ShareSurface to useCopyShareLink() or
// <ShareModal>, and the lib dispatches to the correct onEvent payload
// shape via emitShareEvent.
//
// emitShareEvent is INTERNAL — only consumed by use-copy-share-link.ts,
// share-modal.tsx, and share.ts (the auto-emit wrapping openShareIntent /
// nativeShare).

import type { GiftShareChannel } from '@/events';
import { onEvent } from '@/events';

export type ShareAction =
  | 'copy'
  | 'native'
  | 'x'
  | 'telegram'
  | 'whatsapp'
  | 'reddit'
  | 'discord'
  | 'sms'
  | 'mailto'
  | 'qr'
  | 'follow_discord'
  | 'follow_telegram'
  | 'follow_x'
  | 'follow_reddit';

export type ShareSurface =
  | { kind: 'market'; marketPubkey: string }
  | {
      kind: 'live_event';
      eventId: string;
      title: string;
      bannerImageUrl: string;
      phase: 'live' | 'upcoming' | 'concluded' | 'no-markets-open';
      category: string;
      scheduleLabel: string;
      identityImageUrls: readonly string[];
    }
  | {
      // Trading competition card (Social banner, list rows and the competition
      // page). Display snapshot in the payload, like `live_event`, so the OG
      // route renders without a backend read.
      kind: 'competition';
      competitionId: string;
      title: string;
      phase: 'live' | 'upcoming' | 'concluded';
      /** Formatted prize pool, e.g. "$5K". */
      prizeLabel: string;
      /** End date and time, including its timezone. */
      endLabel: string;
    }
  | {
      // One user's standing in a trading competition (rank bar share and the
      // `?rank=<userId>` unfurl). Snapshot at share time, like `rewards_rank`.
      kind: 'competition_rank';
      competitionId: string;
      userId: string;
      rank: number;
      title: string;
      /** Formatted metric value at share time, e.g. "+$892K". */
      valueLabel: string;
      rankedByLabel: string;
      prizeLabel: string;
      /** Formatted predicted (or final) winnings; `null` outside the top 10. */
      winningsLabel: string | null;
      phase: 'live' | 'concluded';
      handle: string | null;
      displayName: string | null;
      avatarUrl: string | null;
    }
  | { kind: 'proposal'; proposalPubkey: string }
  | { kind: 'gift'; opaqueId?: string; depositPubkey?: string }
  | { kind: 'gift_thank' }
  | { kind: 'support_mainnet' }
  | { kind: 'referral_link'; userId: string }
  | { kind: 'referral_banner'; userId: string }
  | { kind: 'thesis'; signature: string }
  | {
      kind: 'trade';
      signature: string;
      marketPubkey: string;
      userPubkey: string;
      direction: 'buy' | 'sell' | 'swap';
      /** D6-encoded USDC amount as a string (BigInt-safe). */
      amountD6: string;
      outcomeIndex: number;
    }
  | { kind: 'boost'; marketPubkey: string }
  | {
      kind: 'proposal_back';
      proposalPubkey: string;
      userPubkey: string;
      /** D6-encoded USDC amount as a string (BigInt-safe). */
      amountD6: string;
      /** Amount formatted in the user's selected display currency. */
      displayAmount: string;
      outcomeIndex: number;
    }
  | { kind: 'rank'; userId: string; pnlRank: number; volumeRank: number }
  | {
      kind: 'rank_boosters';
      userId: string;
      amountRank: number;
      boostsRank: number;
      marketsBoostedRank: number;
    }
  | {
      kind: 'rank_creators';
      userId: string;
      topMarketRank: number;
      marketsCreatedRank: number;
      totalVolumeRank: number;
    }
  | {
      kind: 'rank_backers';
      userId: string;
      amountRank: number;
      backsRank: number;
      topMarketRank: number;
    }
  | { kind: 'profile'; userId: string }
  | { kind: 'backer_rewards'; userId: string; viewerMode: 'own' | 'other' }
  | { kind: 'streak'; userId: string }
  | { kind: 'globe'; mode: 'market' | 'launch' | 'winner' | 'users' }
  | {
      kind: 'pnl_position';
      marketPubkey: string;
      walletPubkey: string;
      outcomeIndex: number;
      /** Changes with the displayed PnL so every updated share gets a fresh OG image. */
      cacheVersion?: string;
      /** The initiating UI row is in closed history and should render booked PnL. */
      closed?: boolean;
    }
  | {
      kind: 'pnl_market';
      marketPubkey: string;
      walletPubkey: string;
    }
  | {
      // Market resolution hero share — per-wallet card for a RESOLVED market.
      // Distinct from `market` (the generic pre/post-resolution market share)
      // and from `pnl_market` (the portfolio-side aggregate). The card shows
      // the winning outcome + the user's net realized P&L on this market.
      kind: 'market_result';
      marketPubkey: string;
      walletPubkey: string;
    }
  | {
      // Public, wallet-free market RESOLUTION result card — the shareable
      // "answer revealed" hero (question + winning outcome + final winning
      // odds + prize pool + total volume). This is the card the resolution
      // moment shares for everyone (spectators + participants), distinct from
      // `market_result` (per-wallet personal P&L) and `market` (the generic
      // pre/post-resolution market share). Fires `resolution_card_shared`.
      kind: 'market_result_public';
      marketPubkey: string;
    }
  | {
      kind: 'market_prize_pool';
      marketPubkey: string;
      /** D6-encoded prize pool USDC at share time (BigInt-safe string). */
      prizePoolD6: string;
    }
  | {
      kind: 'prize_claim';
      marketPubkey: string;
      walletPubkey: string;
      /** D6-encoded USDC amount the user claimed (BigInt-safe string). */
      amountD6: string;
    }
  | {
      // Winning-token detail page (`/winners/{mint}`). Token-focused share
      // surface — the OG card shows the winning outcome as a tradeable token
      // (name, ticker, image, price), distinct from the parent market's
      // consensus card. `marketPubkey` is carried so the JSON-LD can reuse
      // the market builder (same underlying event).
      kind: 'winner';
      mint: string;
      marketPubkey: string;
    }
  | {
      // Rewards leaderboard standing — a participant shares their rank,
      // points, and level. The share URL is the public rewards profile
      // (`/rewards/profile/<handle>`); the OG card is a query-param
      // snapshot so the unfurl shows the standing at share time.
      kind: 'rewards_rank';
      handle: string;
      rank: number;
      /** Rewards points as a raw integer string (BigInt-safe). */
      points: string;
      levelKey: string;
      levelName: string;
      /** X avatar URL for the card identity; `null` renders the initials disc. */
      avatar: string | null;
    }
  | { kind: 'static_page'; slug: StaticPageSlug };

/** Brand / legal / informational pages that share the same OG card layout
 *  but render slug-specific copy. New static brand pages MUST be added
 *  here AND have a corresponding case in the OG route at
 *  `/api/og/static_page/[slug]/route.tsx`. */
export type StaticPageSlug =
  | 'home'
  | 'markets'
  | 'winners'
  | 'about'
  | 'social'
  | 'competitions'
  | 'launchpad'
  | 'portfolio'
  | 'portfolio_next'
  | 'privacy'
  | 'delete_account'
  | 'terms'
  | 'risk'
  | 'brand'
  | 'compare_polymarket'
  | 'compare_kalshi'
  | 'compare_manifold'
  | 'compare_limitless'
  | 'compare_pumpfun'
  | 'learn_index'
  | 'learn_opinion_markets'
  | 'learn_opinion_vs_prediction'
  | 'learn_bonding_curve'
  | 'learn_how_morfi_resolves'
  | 'learn_graduated_tokens'
  | 'learn_prize_pool'
  | 'learn_launchpad'
  | 'learn_is_gambling'
  | 'markets_category';

/** String shorthand for surfaces with no required payload. Components can
 *  pass either the shorthand or the full object — the lib normalizes to
 *  the canonical object before dispatching. */
export type ShareSurfaceShorthand = 'gift' | 'gift_thank' | 'support_mainnet';

export type ShareSurfaceInput = ShareSurface | ShareSurfaceShorthand;

export function normalizeShareSurface(input: ShareSurfaceInput): ShareSurface {
  if (typeof input === 'string') return { kind: input };
  return input;
}

export function emitShareEvent(surface: ShareSurface, action: ShareAction): void {
  switch (surface.kind) {
    case 'backer_rewards': {
      const method = mapPnlPositionMethod(action);
      if (method === null) return;
      onEvent('backer_rewards_shared', {
        userId: surface.userId,
        viewerMode: surface.viewerMode,
        method,
      });
      return;
    }
    case 'market': {
      if (action === 'copy' || action === 'native') {
        onEvent('share_clicked', { marketPubkey: surface.marketPubkey, method: action });
      }
      return;
    }
    case 'gift': {
      const channel = mapGiftChannel(action);
      if (channel === null) return;
      onEvent('gift_share_clicked', {
        ...(surface.depositPubkey !== undefined ? { depositPubkey: surface.depositPubkey } : {}),
        channel,
      });
      return;
    }
    case 'gift_thank': {
      if (action === 'x') onEvent('gift_share_clicked_x', { channel: 'x' });
      return;
    }
    case 'support_mainnet': {
      const channel = mapSupportChannel(action);
      if (channel === null) return;
      onEvent('support_mainnet_share_clicked', { channel });
      return;
    }
    case 'referral_link': {
      const method = mapReferralMethod(action);
      if (method === null) return;
      onEvent('social_referral_link_copied', { method });
      return;
    }
    case 'thesis': {
      const method = mapThesisMethod(action);
      if (method === null) return;
      onEvent('social_thesis_shared', { signature: surface.signature, method });
      return;
    }
    case 'trade': {
      const method = mapThesisMethod(action);
      if (method === null) return;
      onEvent('social_trade_shared', {
        signature: surface.signature,
        marketPubkey: surface.marketPubkey,
        direction: surface.direction,
        method,
      });
      return;
    }
    case 'boost': {
      const method = mapThesisMethod(action);
      if (method === null) return;
      onEvent('social_boost_shared', {
        activityType: 'boost',
        marketPubkey: surface.marketPubkey,
        method,
      });
      return;
    }
    case 'proposal_back': {
      const method = mapThesisMethod(action);
      if (method === null) return;
      onEvent('social_proposal_back_shared', {
        proposalPubkey: surface.proposalPubkey,
        outcomeIndex: surface.outcomeIndex,
        method,
      });
      return;
    }
    case 'rank': {
      onEvent('social_referral_rank_shared', {
        pnlRank: surface.pnlRank,
        volumeRank: surface.volumeRank,
      });
      return;
    }
    case 'rank_boosters': {
      onEvent('social_referral_rank_boosters_shared', {
        amountRank: surface.amountRank,
        boostsRank: surface.boostsRank,
        marketsBoostedRank: surface.marketsBoostedRank,
      });
      return;
    }
    case 'rank_creators': {
      onEvent('social_referral_rank_creators_shared', {
        topMarketRank: surface.topMarketRank,
        marketsCreatedRank: surface.marketsCreatedRank,
        totalVolumeRank: surface.totalVolumeRank,
      });
      return;
    }
    case 'rank_backers': {
      onEvent('social_referral_rank_backers_shared', {
        amountRank: surface.amountRank,
        backsRank: surface.backsRank,
        topMarketRank: surface.topMarketRank,
      });
      return;
    }
    case 'pnl_position': {
      const method = mapPnlPositionMethod(action);
      if (method === null) return;
      onEvent('pnl_card_shared', {
        marketPubkey: surface.marketPubkey,
        walletPubkey: surface.walletPubkey,
        outcomeIndex: surface.outcomeIndex,
        method,
      });
      return;
    }
    case 'pnl_market': {
      const method = mapPnlPositionMethod(action);
      if (method === null) return;
      onEvent('pnl_market_card_shared', {
        marketPubkey: surface.marketPubkey,
        walletPubkey: surface.walletPubkey,
        method,
      });
      return;
    }
    case 'market_result': {
      const method = mapPnlPositionMethod(action);
      if (method === null) return;
      onEvent('market_result_shared', {
        marketPubkey: surface.marketPubkey,
        walletPubkey: surface.walletPubkey,
        method,
      });
      return;
    }
    case 'market_result_public': {
      const method = mapPnlPositionMethod(action);
      if (method === null) return;
      onEvent('resolution_card_shared', {
        marketPubkey: surface.marketPubkey,
        method,
      });
      return;
    }
    case 'market_prize_pool': {
      if (action === 'copy' || action === 'native') {
        onEvent('share_clicked', { marketPubkey: surface.marketPubkey, method: action });
      }
      return;
    }
    case 'prize_claim': {
      const method = mapPnlPositionMethod(action);
      if (method === null) return;
      onEvent('prize_claim_shared', {
        marketPubkey: surface.marketPubkey,
        walletPubkey: surface.walletPubkey,
        amountD6: surface.amountD6,
        method,
      });
      return;
    }
    case 'proposal': {
      const method = mapProposalMethod(action);
      if (method === null) return;
      onEvent('social_proposal_shared', {
        proposalPubkey: surface.proposalPubkey,
        method,
      });
      return;
    }
    case 'referral_banner':
    case 'live_event':
    case 'profile':
    case 'streak':
    case 'static_page':
    case 'winner':
    case 'globe':
    case 'rewards_rank':
    case 'competition':
    case 'competition_rank':
      // No analytics on these surfaces today.
      return;
  }
}

type PnlPositionMethod = 'copy' | 'native' | 'x' | 'telegram' | 'whatsapp' | 'reddit';

function mapPnlPositionMethod(a: ShareAction): PnlPositionMethod | null {
  switch (a) {
    case 'copy':
    case 'native':
    case 'x':
    case 'telegram':
    case 'whatsapp':
    case 'reddit':
      return a;
    default:
      return null;
  }
}

function mapGiftChannel(a: ShareAction): GiftShareChannel | null {
  switch (a) {
    case 'copy':
      return 'copy_link';
    case 'native':
      return 'native';
    case 'x':
      return 'x';
    case 'telegram':
      return 'telegram';
    case 'whatsapp':
      return 'whatsapp';
    case 'discord':
      return 'discord';
    default:
      return null;
  }
}

type SupportMainnetChannel =
  | 'x'
  | 'telegram'
  | 'discord'
  | 'copy_link'
  | 'follow_discord'
  | 'follow_telegram'
  | 'follow_x'
  | 'follow_reddit';

function mapSupportChannel(a: ShareAction): SupportMainnetChannel | null {
  switch (a) {
    case 'copy':
      return 'copy_link';
    case 'discord':
      return 'discord';
    case 'x':
      return 'x';
    case 'telegram':
      return 'telegram';
    case 'follow_discord':
    case 'follow_telegram':
    case 'follow_x':
    case 'follow_reddit':
      return a;
    default:
      return null;
  }
}

type ReferralMethod = 'copy' | 'native' | 'x' | 'telegram' | 'whatsapp' | 'qr';

function mapReferralMethod(a: ShareAction): ReferralMethod | null {
  switch (a) {
    case 'copy':
    case 'native':
    case 'x':
    case 'telegram':
    case 'whatsapp':
    case 'qr':
      return a;
    default:
      return null;
  }
}

type ThesisMethod = 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp';

function mapThesisMethod(a: ShareAction): ThesisMethod | null {
  switch (a) {
    case 'copy':
    case 'native':
    case 'x':
    case 'telegram':
    case 'whatsapp':
      return a;
    default:
      return null;
  }
}

type ProposalMethod = 'native' | 'copy' | 'x' | 'telegram' | 'whatsapp' | 'reddit';

function mapProposalMethod(a: ShareAction): ProposalMethod | null {
  switch (a) {
    case 'copy':
    case 'native':
    case 'x':
    case 'telegram':
    case 'whatsapp':
    case 'reddit':
      return a;
    default:
      return null;
  }
}
